import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ExternalLink, Sparkles } from 'lucide-react';
import Sticker from './Sticker';
import OrganizerLogo from './OrganizerLogo';
import { useSettings } from '../lib/usePublicData';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_MORE_INFO_URL = 'https://www.ronse.be/nl/zotte-zomer';
const DEFAULT_INFO_TILE_URL = '/assets/zotte-zomer-tile.png';

export const Hero = () => {
    const { data: settings } = useSettings();
    const heroTitle = settings?.hero_title || 'ZOTTE ZOMER';
    const heroSubtitle = settings?.hero_subtitle || 'RONSE 2026';
    const heroIntro = settings?.hero_intro || 'Drie zomerse festivaldagen op de Kleine Markt, in de Stadstuin en op de Grote Markt.';
    const ctaProgram = settings?.cta_program_text || 'Bekijk programma';
    const ctaPartner = settings?.cta_partner_text || 'Word partner';
    const organizerLogoUrl = settings?.organizer_logo_url || '';
    const executorLogoUrl = settings?.executor_logo_url || '';
    const moreInfoUrl = settings?.more_info_url || DEFAULT_MORE_INFO_URL;
    const infoTileSrc = (() => {
        const u = settings?.info_tile_url || settings?.qr_code_url || DEFAULT_INFO_TILE_URL;
        // Relatieve URL voor /api/ paden — browser resolved tegen huidige origin
        return u;
    })();

    return (
        <header
            id="top"
            data-testid="hero-section"
            className="relative isolate bg-vakjes zz-min-h-screen-dvh pt-24 sm:pt-28 pb-16 sm:pb-24 zz-grain"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8 relative">
                <div className="relative bg-cream/80 backdrop-blur-sm border-2 border-[var(--green)] rounded-[36px] p-6 sm:p-12 lg:p-16 shadow-[0_8px_0_var(--green-dark)]">
                    {/* Sticker top-right */}
                    <Sticker
                        variant="green"
                        tilt="r"
                        className="absolute -top-4 right-6 sm:right-10"
                        data-testid="hero-sticker-gratis"
                    >
                        <Sparkles size={14} className="mr-1" /> Gratis toegang
                    </Sticker>

                    {/* Sticker top-left */}
                    <Sticker
                        variant="cream"
                        tilt="l"
                        className="absolute -top-3 left-6 sm:left-10 hidden sm:inline-flex"
                    >
                        Ronse 2026
                    </Sticker>

                    {/* Title — letter-wave (ola) animatie over alle letters van ZOTTE ZOMER, doorlopend van regel 1 → regel 2 */}
                    <div className="text-center pt-2 sm:pt-4">
                        <h1
                            data-testid="hero-title"
                            className="zz-outline-title font-display"
                            style={{ fontSize: 'clamp(3rem, 10vw, 7rem)' }}
                            aria-label={heroTitle}
                        >
                            {(() => {
                                const words = heroTitle.split(' ');
                                let letterIndex = 0;
                                return words.map((word, wi) => (
                                    <React.Fragment key={wi}>
                                        <span className="block sm:inline" aria-hidden="true">
                                            {word.split('').map((char) => {
                                                const i = letterIndex++;
                                                return (
                                                    <span
                                                        key={i}
                                                        className="zz-letter-wave"
                                                        style={{ animationDelay: `${i * 0.08}s` }}
                                                    >
                                                        {char}
                                                    </span>
                                                );
                                            })}
                                        </span>
                                        {wi < words.length - 1 && (
                                            <span className="hidden sm:inline" aria-hidden="true">{'\u00A0'}</span>
                                        )}
                                    </React.Fragment>
                                ));
                            })()}
                        </h1>
                        <p
                            data-testid="hero-subtitle"
                            className="mt-3 sm:mt-5 zz-outline-title-thin font-display"
                            style={{ fontSize: 'clamp(1.5rem, 5vw, 2.75rem)' }}
                        >
                            {heroSubtitle}
                        </p>
                    </div>

                    {/* Body */}
                    <p
                        data-testid="hero-body"
                        className="mt-8 sm:mt-10 text-center text-base sm:text-lg text-[var(--green-dark)] max-w-2xl mx-auto leading-relaxed"
                    >
                        {heroIntro}
                    </p>

                    {/* Organizer strip — Bright Events hoofdorganisator (groter), Stad Ronse met de steun van (kleiner) */}
                    <div
                        data-testid="hero-organizer-strip"
                        className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5 text-[var(--green-dark)]"
                    >
                        <div className="flex items-center gap-2.5">
                            <span className="font-display text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[var(--green-dark)]/70">
                                Een organisatie door
                            </span>
                            <OrganizerLogo url={executorLogoUrl} variant="bright" size="md" testId="hero-org-bright" />
                        </div>
                        <span className="hidden sm:inline-block w-px h-6 bg-[var(--green)]/30" aria-hidden="true" />
                        <div className="flex items-center gap-2.5">
                            <span className="font-display text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[var(--green-dark)]/70">
                                met de steun van
                            </span>
                            <OrganizerLogo url={organizerLogoUrl} variant="stad" size="sm" testId="hero-org-stad" />
                        </div>
                    </div>

                    {/* Date pills */}
                    <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                        <span className="zz-pill" data-testid="hero-date-1">11 juli</span>
                        <span className="zz-pill" data-testid="hero-date-2">25 juli</span>
                        <span className="zz-pill" data-testid="hero-date-3">29 augustus</span>
                    </div>

                    {/* CTAs */}
                    <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-5">
                        <Link
                            to="/#programma"
                            data-testid="hero-cta-program"
                            className="zz-btn zz-btn--primary"
                        >
                            {ctaProgram} <ArrowRight size={18} />
                        </Link>
                        <Link
                            to="/#word-partner"
                            data-testid="hero-cta-partner"
                            className="zz-btn zz-btn--cream"
                        >
                            {ctaPartner}
                        </Link>
                    </div>

                    {/* Meer info — Zotte Zomer logo-tile met sticker-badge, links naar Stad Ronse Zotte Zomer pagina */}
                    <a
                        href={moreInfoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="hero-qr-link"
                        aria-label="Meer info over Zotte Zomer op ronse.be (opent in nieuw tabblad)"
                        className="group mt-10 sm:mt-0 sm:absolute sm:bottom-6 sm:right-6 inline-flex flex-col items-center sm:items-end gap-0 outline-none focus-visible:ring-2 focus-visible:ring-[var(--green-dark)] focus-visible:ring-offset-2 rounded-2xl transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
                    >
                        <div
                            data-testid="hero-info-tile"
                            className="relative zz-tilt-r bg-cream border-2 border-[var(--green)] rounded-2xl p-1.5 shadow-[0_4px_0_var(--green-dark)] group-hover:shadow-[0_6px_0_var(--green-dark)] transition-shadow duration-200"
                        >
                            <img
                                src={infoTileSrc}
                                alt="Zotte Zomer logo"
                                width={128}
                                height={128}
                                fetchPriority="high"
                                loading="eager"
                                decoding="async"
                                className="block w-[96px] h-[96px] sm:w-[128px] sm:h-[128px] object-contain rounded-xl"
                            />
                            <span
                                data-testid="hero-meer-info-sticker"
                                aria-hidden="true"
                                className="zz-sticker zz-sticker--green zz-tilt-l font-display absolute -bottom-3 -left-3 inline-flex items-center gap-1 !text-[10px] sm:!text-xs !py-1 !px-2.5 shadow-[0_2px_0_var(--green-dark)] group-hover:shadow-[0_3px_0_var(--green-dark)] transition-shadow duration-200"
                            >
                                MEER INFO
                                <ExternalLink size={11} aria-hidden="true" />
                            </span>
                        </div>
                    </a>
                </div>
            </div>
        </header>
    );
};

export default Hero;
